import { trialByPlanV2_18369Variant } from '@/services/ab/experiments'
import { platform } from '../platform'

export interface PlatformTrialPolicy {
  plan_name: string
  trial_days: number | null
}

interface TrialPolicy {
  web: PlatformTrialPolicy
  ios: PlatformTrialPolicy
}

export interface TrialByPlanExperimentVariantConfig {
  name: string
  monthly: TrialPolicy
  annual: TrialPolicy
}

/**
 * Get the configuration for the active trial-by-plan experiment variant.
 *
 * @param variant: A, B
 * @returns experiment configuration for the variant.
 */
export function getTrialByPlanVariantConfig(
  variant: string,
): TrialByPlanExperimentVariantConfig {
  return getTrialByPlanV2_18369VariantConfig(variant)
}

/**
 * Get the configuration for the trial-by-plan v2 experiment variant.
 *
 * V2 variant B is the previous experiment's variant C.
 */
export function getTrialByPlanV2_18369VariantConfig(
  variant: string,
): TrialByPlanExperimentVariantConfig {
  const variantConfig = TRIAL_BY_PLAN_V2_VARIANTS[variant]

  if (!variantConfig) {
    throw new Error(`Unknown trial variant: ${variant}`)
  }
  return variantConfig
}

/**
 * Get the trial policy for the given variant and platform.
 * @param variant: A, B
 * @param platform: web, ios
 * @param planType: monthly, annual
 * @returns trial policy for the variant and platform.
 */
export async function getTrialPolicy(
  variant: string,
  planType: 'monthly' | 'annual',
): Promise<PlatformTrialPolicy> {
  const config = getTrialByPlanV2_18369VariantConfig(variant)
  const isNativeIOSApp = await platform.isNativeIOSApp()
  const platformConfig = isNativeIOSApp ? 'ios' : 'web'
  return config[planType][platformConfig]
}

/**
 * Get iOS plan name for the variant assigned to user.
 * @returns plan name for App Store.
 */
export function trialByPlanIOSPlanName(
  isMonthly: boolean = true,
): string | null {
  // Default to 'A' Variant of the experiment
  const trialByPlan = trialByPlanV2_18369Variant()
  if (!trialByPlan) {
    return null
  }
  const config = getTrialByPlanV2_18369VariantConfig(trialByPlan)
  if (isMonthly) {
    return config.monthly.ios.plan_name
  } else {
    return config.annual.ios.plan_name
  }
}

/**
 * Returns true if the user has the treatment variant in trial-by-plan v2.
 */
export function trialByPlanV2_18369Treatment(): boolean {
  const variant = trialByPlanV2_18369Variant()

  return variant === 'B'
}

const TRIAL_BY_PLAN_V2_VARIANTS: Record<
  string,
  TrialByPlanExperimentVariantConfig
> = {
  A: {
    name: 'A',
    monthly: {
      web: {
        trial_days: 5,
        plan_name: 'plan_monthly',
      },
      ios: {
        trial_days: 7,
        plan_name: 'shortform_monthly',
      },
    },
    annual: {
      web: {
        trial_days: 5,
        plan_name: 'plan_annual',
      },
      ios: {
        trial_days: 7,
        plan_name: 'shortform_annual',
      },
    },
  },
  B: {
    name: 'B',
    monthly: {
      web: {
        trial_days: null,
        plan_name: 'plan_monthly',
      },
      ios: {
        trial_days: null,
        plan_name: 'shortform_monthly_no_trial',
      },
    },
    annual: {
      web: {
        trial_days: 14,
        plan_name: 'plan_annual',
      },
      ios: {
        trial_days: 14,
        plan_name: 'shortform_annual_2_week_trial',
      },
    },
  },
}
